import React, { useState } from 'react';
import { Heart, Users, BookOpen, Scissors, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { processDonation } from '../services/api';

const Donate = () => {
  const { toast } = useToast();
  const [donationType, setDonationType] = useState('monthly');
  const [amount, setAmount] = useState('25');
  const [customAmount, setCustomAmount] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    amount: ''
  });

  const monthlyAmounts = ['10', '25', '50', '100'];
  const oneTimeAmounts = ['25', '50', '100', '250', '500'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const donationAmount = customAmount || amount;
    
    const result = await processDonation({
      type: donationType,
      amount: donationAmount,
      name: formData.name,
      email: formData.email
    });
    
    if (result.success) {
      toast({
        title: "Thank You!",
        description: result.message,
      });
      // Reset form
      setFormData({ name: '', email: '', amount: '' });
      setCustomAmount('');
    } else {
      toast({
        title: "Error",
        description: result.message,
        variant: "destructive"
      });
    }
  };

  return (
    <div className="bg-white min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-[#0A277A] to-[#3F54C8] text-white">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10 text-center">
          <Heart className="mx-auto mb-6" size={64} />
          <h1 className="font-black text-5xl md:text-7xl uppercase mb-6">
            Support Our Mission
          </h1>
          <p className="text-white/90 text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed">
            Help us empower veterans and families through education, vocational training, and community support. Every donation makes a lasting impact.
          </p>
        </div>
      </section>

      {/* Impact Areas */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="text-center mb-16">
            <h2 className="font-black text-4xl md:text-6xl text-[#0A277A] uppercase mb-4">
              Your Impact
            </h2>
            <p className="text-gray-600 text-lg">
              Donations support both tracks of our nonprofit ecosystem
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-white border-2 border-[#0A277A] p-8 text-center hover:shadow-xl transition-all">
              <BookOpen className="mx-auto mb-6 text-[#0A277A]" size={48} />
              <h3 className="font-black text-xl text-[#0A277A] uppercase mb-4">
                Track 1: Personal Mastery
              </h3>
              <ul className="text-gray-700 text-sm space-y-2 text-left">
                <li className="flex items-start">
                  <Check size={16} className="text-[#0A277A] mr-2 mt-1 flex-shrink-0" />
                  <span>Fund MOS Book Boxes</span>
                </li>
                <li className="flex items-start">
                  <Check size={16} className="text-[#0A277A] mr-2 mt-1 flex-shrink-0" />
                  <span>Support virtual discussions & workshops</span>
                </li>
                <li className="flex items-start">
                  <Check size={16} className="text-[#0A277A] mr-2 mt-1 flex-shrink-0" />
                  <span>Sponsor MOS Circle programs</span>
                </li>
              </ul>
            </Card>

            <Card className="bg-white border-2 border-[#D80000] p-8 text-center hover:shadow-xl transition-all">
              <Scissors className="mx-auto mb-6 text-[#D80000]" size={48} />
              <h3 className="font-black text-xl text-[#0A277A] uppercase mb-4">
                Track 2: Mastery Through Trade
              </h3>
              <ul className="text-gray-700 text-sm space-y-2 text-left">
                <li className="flex items-start">
                  <Check size={16} className="text-[#D80000] mr-2 mt-1 flex-shrink-0" />
                  <span>Provide scholarships for barber training</span>
                </li>
                <li className="flex items-start">
                  <Check size={16} className="text-[#D80000] mr-2 mt-1 flex-shrink-0" />
                  <span>Fund equipment & training materials</span>
                </li>
                <li className="flex items-start">
                  <Check size={16} className="text-[#D80000] mr-2 mt-1 flex-shrink-0" />
                  <span>Support job placement programs</span>
                </li>
              </ul>
            </Card>

            <Card className="bg-white border-2 border-[#3F54C8] p-8 text-center hover:shadow-xl transition-all">
              <Users className="mx-auto mb-6 text-[#3F54C8]" size={48} />
              <h3 className="font-black text-xl text-[#0A277A] uppercase mb-4">
                Community Programs
              </h3>
              <ul className="text-gray-700 text-sm space-y-2 text-left">
                <li className="flex items-start">
                  <Check size={16} className="text-[#3F54C8] mr-2 mt-1 flex-shrink-0" />
                  <span>Organize MOS Summit events</span>
                </li>
                <li className="flex items-start">
                  <Check size={16} className="text-[#3F54C8] mr-2 mt-1 flex-shrink-0" />
                  <span>Facilitate peer mentorship circles</span>
                </li>
                <li className="flex items-start">
                  <Check size={16} className="text-[#3F54C8] mr-2 mt-1 flex-shrink-0" />
                  <span>Build veteran support networks</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <Card className="bg-gray-50 border-2 border-gray-200 p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="font-black text-3xl md:text-4xl text-[#0A277A] uppercase mb-4">
                Make Your Donation
              </h2>
              <p className="text-gray-600">
                Choose your giving preference below
              </p>
            </div>

            {/* Donation Type Toggle */}
            <div className="flex gap-4 mb-8">
              <button
                onClick={() => setDonationType('monthly')}
                className={`flex-1 py-4 px-6 rounded-lg font-bold uppercase transition-all ${
                  donationType === 'monthly'
                    ? 'bg-[#D80000] text-white shadow-lg'
                    : 'bg-white text-gray-700 border-2 border-gray-300'
                }`}
              >
                Give Monthly
              </button>
              <button
                onClick={() => setDonationType('onetime')}
                className={`flex-1 py-4 px-6 rounded-lg font-bold uppercase transition-all ${
                  donationType === 'onetime'
                    ? 'bg-[#D80000] text-white shadow-lg'
                    : 'bg-white text-gray-700 border-2 border-gray-300'
                }`}
              >
                One-Time Gift
              </button>
            </div>

            {/* Amount Selection */}
            <div className="mb-8">
              <Label className="text-[#0A277A] font-bold uppercase text-sm mb-3 block">
                Select Amount {donationType === 'monthly' && '(Minimum $10/month)'}
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {(donationType === 'monthly' ? monthlyAmounts : oneTimeAmounts).map((amt) => (
                  <button
                    key={amt}
                    onClick={() => {
                      setAmount(amt);
                      setCustomAmount('');
                    }}
                    className={`py-4 px-6 rounded-lg font-bold text-xl transition-all ${
                      amount === amt && !customAmount
                        ? 'bg-[#0A277A] text-white shadow-lg'
                        : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-[#0A277A]'
                    }`}
                  >
                    ${amt}
                  </button>
                ))}
              </div>
              <div>
                <Label className="text-gray-700 text-sm mb-2 block">Or enter custom amount</Label>
                <Input
                  type="number"
                  min={donationType === 'monthly' ? '10' : '1'}
                  placeholder={donationType === 'monthly' ? 'Minimum $10' : 'Enter amount'}
                  value={customAmount}
                  onChange={(e) => {
                    setCustomAmount(e.target.value);
                    setAmount('');
                  }}
                  className="text-lg"
                />
              </div>
            </div>

            {/* Donor Information */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-[#0A277A] font-semibold uppercase text-sm mb-2">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="border-gray-300"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-[#0A277A] font-semibold uppercase text-sm mb-2">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="border-gray-300"
                />
              </div>

              <div className="bg-[#0A277A]/5 p-6 rounded-lg border-l-4 border-[#0A277A]">
                <p className="text-sm text-gray-700">
                  <strong>Note:</strong> This is a demonstration form. Stripe payment integration will be added to process real donations. Your information is for demonstration purposes only.
                </p>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#D80000] text-white hover:bg-[#b00000] rounded-full py-6 text-lg font-semibold uppercase transition-all hover:scale-105"
              >
                {donationType === 'monthly' ? 'Start Monthly Giving' : 'Complete Donation'}
              </Button>

              <p className="text-gray-600 text-sm text-center leading-relaxed">
                Mastery of Self (M.O.S.) is a tax exempt charitable organization (federal tax ID #85-2610451). All contributions are tax-deductible to the extent allowable by law.
              </p>
            </form>
          </Card>
        </div>
      </section>

      {/* Other Ways to Give */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <h2 className="font-black text-4xl md:text-5xl text-[#0A277A] uppercase mb-4">
              Other Ways to Support
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-white p-8 text-center">
              <h3 className="font-black text-xl text-[#0A277A] uppercase mb-4">
                Sponsor a Student
              </h3>
              <p className="text-gray-700 mb-6">
                Directly fund a veteran's barber school tuition
              </p>
              <Button className="bg-[#D80000] text-white hover:bg-[#b00000] rounded-full px-6 py-3 font-semibold uppercase w-full">
                Learn More
              </Button>
            </Card>

            <Card className="bg-white p-8 text-center">
              <h3 className="font-black text-xl text-[#0A277A] uppercase mb-4">
                Corporate Partnership
              </h3>
              <p className="text-gray-700 mb-6">
                Partner with us to support veteran programs
              </p>
              <Button className="bg-[#0A277A] text-white hover:opacity-90 rounded-full px-6 py-3 font-semibold uppercase w-full">
                Contact Us
              </Button>
            </Card>

            <Card className="bg-white p-8 text-center">
              <h3 className="font-black text-xl text-[#0A277A] uppercase mb-4">
                Volunteer
              </h3>
              <p className="text-gray-700 mb-6">
                Share your time and expertise with our community
              </p>
              <Button className="bg-[#3F54C8] text-white hover:opacity-90 rounded-full px-6 py-3 font-semibold uppercase w-full">
                Get Involved
              </Button>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;